import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ujmsFinanceDashboard, ujmsDisbursements, ujmsCreateDisbursement, ujmsDisbursementAction, ujmsCases,
} from "../../services/ujmsApi";
import { Panel, Badge, Stat, thCls, tdCls, btnGhost, btnPrimary, inputCls, selectCls, Field, fmtDT, can } from "../ui";
import { useOutletContext } from "react-router-dom";
import { FinanceActionDialog } from "./FinanceDialogs";
import { Banknote, Send } from "lucide-react";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const amtCls = (v) => (v < 0 ? "text-emerald-700" : "text-slate-900");

export default function FinanceDashboard() {
  const { access } = useOutletContext();
  const [data, setData] = useState(null);
  const [action, setAction] = useState(null); // {type, account}

  const load = useCallback(() => {
    ujmsFinanceDashboard().then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="text-sm text-slate-500 p-4">Loading finance dashboard…</div>;
  const manage = can(access, "finance.manage");
  const disburse = can(access, "finance.disburse");

  return (
    <div className="space-y-4" data-testid="finance-dashboard">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><Banknote className="w-6 h-6 text-emerald-700" /> Court Finance</h1>
        <p className="text-sm text-slate-500">Ledger-style court accounting — balances are derived from immutable transactions, never overwritten.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total Assessed" value={money(data.total_assessed)} testId="fin-stat-assessed" />
        <Stat label="Collected" value={money(data.total_collected)} testId="fin-stat-collected" />
        <Stat label="Outstanding" value={money(data.total_outstanding)} testId="fin-stat-outstanding" tone={data.total_outstanding > 0 ? "warn" : undefined} />
        <Stat label="Waived" value={money(data.total_waived)} testId="fin-stat-waived" />
        <Stat label="Restitution Owed" value={money(data.restitution_outstanding)} testId="fin-stat-restitution" />
      </div>

      <Panel title="Open accounts" testId="fin-accounts">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Case</th><th className={thCls}>Party</th><th className={thCls}>Type</th>
            <th className={thCls}>Beneficiary</th><th className={thCls}>Balance</th><th className={thCls}></th></tr></thead>
          <tbody>
            {data.open_accounts.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50" data-testid={`fin-account-${a.id}`}>
                <td className={`${tdCls} font-mono text-xs`}><Link className="text-blue-900 hover:underline" to={`/ujms/cases/${a.case_id}?tab=Finance`}>{a.case_number}</Link></td>
                <td className={tdCls}>{a.party_name || "—"}</td>
                <td className={`${tdCls} text-xs`}><Badge value={a.account_type} /></td>
                <td className={`${tdCls} text-xs`}>{a.beneficiary || "—"}</td>
                <td className={`${tdCls} font-mono font-semibold`} data-testid={`fin-balance-${a.id}`}>{money(a.balance)}</td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  {manage && (
                    <>
                      <button className={btnGhost} onClick={() => setAction({ type: "assess", account: a })} data-testid={`fin-assess-${a.id}`}>Assess</button>
                      <button className={`${btnGhost} ml-1 border-emerald-400 text-emerald-700`} onClick={() => setAction({ type: "pay", account: a })} data-testid={`fin-pay-${a.id}`}>Payment</button>
                      <button className={`${btnGhost} ml-1`} onClick={() => setAction({ type: "adjust", account: a })} data-testid={`fin-adjust-${a.id}`}>Adjust</button>
                      <button className={`${btnGhost} ml-1`} onClick={() => setAction({ type: "waive", account: a })} data-testid={`fin-waive-${a.id}`}>Waive</button>
                      <button className={`${btnGhost} ml-1`} onClick={() => setAction({ type: "plan", account: a })} data-testid={`fin-plan-${a.id}`}>Plan</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!data.open_accounts.length && <tr><td colSpan={6} className={tdCls}>No open financial accounts.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Panel title="Recent ledger activity" testId="fin-ledger" className="xl:col-span-2">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>When</th><th className={thCls}>Case</th><th className={thCls}>Type</th>
              <th className={thCls}>Amount</th><th className={thCls}>Reason</th><th className={thCls}>Actor</th></tr></thead>
            <tbody>{data.recent_transactions.map((t) => (
              <tr key={t.id} data-testid={`fin-txn-${t.id}`}>
                <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(t.created_at)}</td>
                <td className={`${tdCls} font-mono text-xs`}>{t.case_number}</td>
                <td className={tdCls}><Badge value={t.type} />{t.reversed && <span className="ml-1 text-[10px] font-mono text-red-600">REVERSED</span>}</td>
                <td className={`${tdCls} font-mono text-xs ${amtCls(t.amount)}`}>{t.amount < 0 ? "−" : "+"}{money(Math.abs(t.amount))}</td>
                <td className={`${tdCls} text-xs max-w-xs truncate`}>{t.reason}{t.receipt_number && <span className="text-slate-400"> · {t.receipt_number}</span>}</td>
                <td className={`${tdCls} text-xs`}>{t.actor}</td>
              </tr>
            ))}</tbody>
          </table>
        </Panel>
        <Panel title="Active payment plans" testId="fin-plans">
          <table className="w-full border-collapse"><tbody>
            {data.active_plans.map((p) => (
              <tr key={p.id}><td className={tdCls}>{p.party_name}<div className="text-xs text-slate-400">Next due {p.next_due}</div></td>
                <td className={`${tdCls} font-mono text-xs`}>{money(p.monthly_amount)}/mo</td></tr>
            ))}
            {!data.active_plans.length && <tr><td className={tdCls}>No active plans.</td></tr>}
          </tbody></table>
        </Panel>
      </div>

      <DisbursementsPanel manage={disburse} />

      <FinanceActionDialog action={action} onClose={() => setAction(null)} onDone={() => { setAction(null); load(); }} />
    </div>
  );
}

function DisbursementsPanel({ manage }) {
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ case_number: "", payee_name: "", amount: "", method: "CHECK", category: "RESTITUTION", memo: "" });
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    ujmsDisbursements().then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const cr = await ujmsCases({ case_number: f.case_number.trim(), page_size: 1 });
      const items = cr.data.items || cr.data;
      const target = (items || []).find((c) => c.case_number === f.case_number.trim());
      if (!target) { toast.error(`Case ${f.case_number} not found`); setBusy(false); return; }
      await ujmsCreateDisbursement(target.id, {
        payee_name: f.payee_name, amount: Number(f.amount), method: f.method, category: f.category, memo: f.memo });
      toast.success(`Disbursement requested — ${f.payee_name}`);
      setF({ case_number: "", payee_name: "", amount: "", method: "CHECK", category: "RESTITUTION", memo: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const act = async (d, action) => {
    const notes = action === "VOID" ? window.prompt("Void notes (required):") : "";
    if (action === "VOID" && !notes) return;
    try {
      const r = await ujmsDisbursementAction(d.id, action, notes || "");
      toast.success(`Disbursement ${r.data.status}${r.data.reference_number ? ` — Ref ${r.data.reference_number}` : ""}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Panel title="disbursements — restitution, bond refunds & fee refunds paid out" testId="fin-disbursements">
      {manage && (
        <div className="p-3 flex flex-wrap items-end gap-2 border-b border-slate-200">
          <Field label="Case number"><input className={`${inputCls} w-36 font-mono`} value={f.case_number}
            onChange={(e) => setF({ ...f, case_number: e.target.value.toUpperCase() })} placeholder="26CR700001" data-testid="disb-case-input" /></Field>
          <Field label="Payee"><input className={`${inputCls} w-44`} value={f.payee_name}
            onChange={(e) => setF({ ...f, payee_name: e.target.value })} placeholder="e.g. Maria Gonzalez" data-testid="disb-payee-input" /></Field>
          <Field label="Amount"><input type="number" min="0.01" step="0.01" className={`${inputCls} w-28`} value={f.amount}
            onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="disb-amount-input" /></Field>
          <Field label="Category"><select className={`${selectCls} w-36`} value={f.category}
            onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="disb-category-select">
            {["RESTITUTION", "BOND_REFUND", "FEE_REFUND", "OTHER"].map((c) => <option key={c} value={c}>{c.replace("_", " ")}</option>)}
          </select></Field>
          <Field label="Method"><select className={`${selectCls} w-24`} value={f.method}
            onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="disb-method-select">
            <option value="CHECK">CHECK</option><option value="EFT">EFT</option>
          </select></Field>
          <button className={btnPrimary} disabled={busy || !f.case_number.trim() || !f.payee_name.trim() || !Number(f.amount)}
            onClick={create} data-testid="disb-create-btn"><Send className="w-3.5 h-3.5" /> Request</button>
        </div>
      )}
      <table className="w-full border-collapse">
        <thead><tr><th className={thCls}>Requested</th><th className={thCls}>Case</th><th className={thCls}>Payee</th>
          <th className={thCls}>Amount</th><th className={thCls}>Category</th><th className={thCls}>Method</th>
          <th className={thCls}>Status</th><th className={thCls}>Ref</th><th className={thCls}></th></tr></thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.id} className="hover:bg-slate-50" data-testid={`disb-row-${d.id}`}>
              <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(d.created_at)}</td>
              <td className={`${tdCls} font-mono text-xs`}><Link className="text-blue-900 hover:underline" to={`/ujms/cases/${d.case_id}`}>{d.case_number}</Link></td>
              <td className={tdCls}>{d.payee_name}</td>
              <td className={`${tdCls} font-mono text-xs font-semibold`}>${Number(d.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
              <td className={`${tdCls} text-xs`}>{d.category.replace("_", " ")}</td>
              <td className={`${tdCls} text-xs`}>{d.method}</td>
              <td className={tdCls}><Badge value={d.status} /></td>
              <td className={`${tdCls} font-mono text-xs`}>{d.reference_number || "—"}</td>
              <td className={`${tdCls} whitespace-nowrap`}>
                {manage && d.status === "PENDING" && <button className={btnGhost} onClick={() => act(d, "APPROVE")} data-testid={`disb-approve-${d.id}`}>Approve</button>}
                {manage && d.status === "APPROVED" && <button className={`${btnGhost} border-emerald-400 text-emerald-700`} onClick={() => act(d, "PAY")} data-testid={`disb-pay-${d.id}`}>Pay</button>}
                {manage && ["PENDING", "APPROVED"].includes(d.status) && <button className={`${btnGhost} ml-1 border-red-300 text-red-700`} onClick={() => act(d, "VOID")} data-testid={`disb-void-${d.id}`}>Void</button>}
              </td>
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={9} className={tdCls}>No disbursements yet. Pay out restitution, bond refunds and fee refunds from here — every payment is docketed.</td></tr>}
        </tbody>
      </table>
    </Panel>
  );
}
